import { useEffect, type RefObject } from 'react';

/* Kopieerknop op elk codeblok — zat in de EJS-artikelpagina
   (addCopyButtonsToCodeBlocks) en in de beheervoorvertoning
   (enhanceCodeBlocks), en in beide gevallen op dezelfde manier: een knop
   rechtsboven in de <pre>, die na het kopiëren twee seconden "Gekopieerd"
   toont.

   WAAROM DOM-manipulatie en geen React-componenten: de artikelinhoud komt als
   HTML-string binnen (dangerouslySetInnerHTML) omdat de saneerlaag op de
   server de grens is. Daar zit geen React-boom onder waar je een portal in
   kunt hangen, dus de knop wordt hier toegevoegd en bij het opruimen weer
   weggehaald. */

const LABEL = 'Kopieer';
const DONE = 'Gekopieerd';

export function CodeCopyButtons({ scopeRef }: { scopeRef: RefObject<HTMLElement | null> }) {
  useEffect(() => {
    const root = scopeRef.current;
    if (!root) return;

    const timers: ReturnType<typeof setTimeout>[] = [];
    const added: HTMLButtonElement[] = [];

    for (const pre of root.querySelectorAll<HTMLPreElement>('pre')) {
      if (pre.querySelector('[data-code-copy]')) continue;

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.dataset.codeCopy = '';
      btn.textContent = LABEL;
      btn.setAttribute('aria-label', 'Code kopiëren');
      // kb-no-print: op papier is een kopieerknop een raadsel.
      btn.className =
        'kb-no-print absolute top-2 right-2 rounded-md border border-hairline-strong ' +
        'bg-raised/90 px-2 py-1 text-xs font-medium text-ink-2 opacity-0 transition ' +
        'hover:border-accent hover:text-ink focus-visible:opacity-100 group-hover/pre:opacity-100';

      btn.addEventListener('click', async () => {
        // De knop staat ín de <pre>, dus zijn eigen label zou meekomen in
        // textContent. Vandaar de code uit het <code>-kind, met de <pre> als
        // terugval voor blokken zonder.
        const source = pre.querySelector('code') ?? pre;
        const text = source === pre
          ? [...pre.childNodes].filter((n) => n !== btn).map((n) => n.textContent ?? '').join('')
          : source.textContent ?? '';
        try {
          await navigator.clipboard.writeText(text);
          btn.textContent = DONE;
          btn.dataset.copied = 'true';
        } catch {
          btn.textContent = 'Kopiëren mislukt';
        }
        timers.push(setTimeout(() => {
          btn.textContent = LABEL;
          delete btn.dataset.copied;
        }, 2000));
      });

      // group/pre + relative: de knop verschijnt bij hover over het blok en
      // blijft bereikbaar met het toetsenbord (focus-visible hierboven).
      pre.classList.add('group/pre', 'relative');
      pre.appendChild(btn);
      added.push(btn);
    }

    return () => {
      for (const t of timers) clearTimeout(t);
      for (const btn of added) btn.remove();
    };
  }, [scopeRef]);

  return null;
}
